import { computed, Ref, ref } from 'vue'
import { defineStore } from 'pinia'
import type { DocumentNode } from 'graphql'
import { fetchManagedProduct as fetchManagedProductQuery } from '@/queries'
import {
  ClientRelease,
  ManagedClientReleaseResult,
  ManagedProductContentResult,
  ManagedProductDownload,
  ManagedProductContent,
  Product,
  ProductDownloadMap,
  ProductId,
  ScreenName,
  ProductConfirmationVideo,
  ManagedProductConfirmationVideo
} from '@/types'
import { useLazyQuery } from '@vue/apollo-composable'
import getAssetProxy from '@/utils/getAssetProxy'

const baseState = {
  id: '',
  screens: [],
  checkoutSurveys: '',
  nfrPlanSet: '',
  planSet: '',
  viewDecorations: [],
  name: '',
  confirmationFeatures: [],
  confirmationVideo: {},
  confirmationHeaderImage: '',
  releasesForDownload: [],
  showYamahaSignIn: false
}

export const useProductStore = defineStore('ProductStore', () => {
  const product: Ref<Product> = ref(baseState)

  const { onResult: onFetchProductResult, load: initialFetchManagedProduct, refetch: refetchProduct } = useLazyQuery(fetchManagedProductQuery)

  const fetchManagedProduct = (document: DocumentNode, variables: any, options: any) =>
    initialFetchManagedProduct(document, variables, options) || refetchProduct(variables)

  const reduceManagedClientRelease = (managedReleaseUuid: string, rels: any): ClientRelease | undefined => {
    const managedClientRelease = rels.find((relation: any) => relation.uuid === managedReleaseUuid)

    if (!managedClientRelease?.content) {
      return
    }

    const { releasePlatform, version, releaseNotes: notes } = managedClientRelease?.content as ManagedClientReleaseResult

    const clientRelease = releasePlatform.reduce((platforms: ClientRelease, { platform, installer, updater, installerSize, updaterSize, minimum }) => {
      return {
        ...platforms,
        [platform]: {
          installer: installer.url,
          updater: updater.url,
          installerSize,
          updaterSize,
          minimum
        }
      }
    }, {} as ClientRelease)

    return {
      notes,
      ...clientRelease,
      version
    }
  }

  const reduceProductDownloads = (downloadUuids: Array<string>, rels: any): ProductDownloadMap[] => {
    const productDownloads = downloadUuids.map((downloadUuid: string) => {
      const download = rels.find((relation: any) => relation.uuid === downloadUuid)
      const { content, slug: id } = download
      const { name, method, badge, value, managedClientRelease: managedClientReleaseReference } = content as ManagedProductDownload

      const downloadValue = reduceManagedClientRelease(managedClientReleaseReference, rels)

      return {
        id,
        name,
        badge,
        method,
        value: downloadValue || value
      }
    })

    return productDownloads
  }

  const reduceProductConfirmationVideo = (confirmationVideoUuid: string | undefined, rels: any): ProductConfirmationVideo | undefined => {
    if (!confirmationVideoUuid) {
      return
    }

    const confirmationVideo = rels.find((relation: any) => relation.uuid === confirmationVideoUuid)
    const { content, slug: id } = confirmationVideo
    const { buttonText, description, sectionAnchorLink, videoUrl, displayVideoControls, thumbnail } = content as ManagedProductConfirmationVideo

    const url = videoUrl?.filename || ''
    const thumbnailFilename = thumbnail?.filename || ''

    return {
      id,
      buttonText,
      description,
      sectionAnchorLink,
      url,
      displayVideoControls,
      thumbnail: thumbnailFilename
    }
  }

  const reduceProductStateResponse = (response: ManagedProductContentResult): Product => {
    const {
      rels,
      story: { slug: id, content }
    } = response
    const {
      checkoutSurveys,
      confirmationFeaturesList: confirmationFeatures,
      confirmationVideo: confirmationVideoUuid,
      productLogo,
      name,
      productDownloads,
      nfrPlanSet,
      subscriptionPlanSet: planSet
    } = content as ManagedProductContent

    const releasesForDownload = reduceProductDownloads(productDownloads, rels)
    const confirmationVideo = reduceProductConfirmationVideo(confirmationVideoUuid, rels)
    const confirmationHeaderImage = productLogo?.filename ? getAssetProxy(productLogo?.filename) : ''

    return {
      checkoutSurveys,
      confirmationFeatures,
      confirmationVideo,
      confirmationHeaderImage,
      id,
      name,
      nfrPlanSet,
      planSet,
      releasesForDownload
    }
  }

  const setProduct = (productId: ProductId) => {
    const isProductIdMatchExistingId = product.value.id === productId

    if (product.value.id && isProductIdMatchExistingId) {
      return
    }

    // Set the ID for downstream checks
    product.value = {
      ...product.value,
      id: productId
    }

    const version = import.meta.env.VITE_OUTPUT_ENVIRONMENT !== 'production' ? 'draft' : 'published'
    const storyId = `application-resources/product-store-state/${productId}`

    return fetchManagedProduct(
      fetchManagedProductQuery,
      {
        storyId,
        version,
        relations: 'productDownloads,product-download.managedClientRelease,confirmationVideo'
      },
      {
        errorPolicy: 'ignore',
        fetchPolicy: 'no-cache',
        context: {
          fetchOptions: {
            method: 'GET'
          }
        }
      }
    )
  }

  onFetchProductResult(result => {
    if (!result?.data) {
      return
    }
    const { fetchManagedProductContent } = result.data

    const productContent = reduceProductStateResponse(fetchManagedProductContent)

    product.value = {
      ...product.value,
      ...productContent
    }
  })

  const resetProduct = () => {
    product.value = {
      ...baseState
    }
  }

  const confirmationVideo = computed(() => product.value.confirmationVideo)
  const confirmationFeatures = computed(() => product.value.confirmationFeatures)
  const confirmationHeaderImage = computed(() => product.value.confirmationHeaderImage)
  const checkoutSurveyGroup = computed(() => product.value.checkoutSurveys)
  const name = computed(() => product.value?.name)
  const productId = computed(() => product.value?.id)
  const productName = computed(() => product.value?.name)
  const planSet = computed(() => product.value?.planSet)
  const nfrPlanSet = computed(() => product.value?.nfrPlanSet)
  const releasesForDownload = computed(() => product.value.releasesForDownload)
  const viewDecorations = computed(() => product.value.viewDecorations)

  const screens = computed(() => [
    {
      id: ScreenName.Confirmation,
      params: {
        productName: productId.value
      }
    }
  ])

  return {
    checkoutSurveyGroup,
    confirmationFeatures,
    confirmationHeaderImage,
    confirmationVideo,
    name,
    nfrPlanSet,
    planSet,
    product,
    productId,
    productName,
    resetProduct,
    releasesForDownload,
    screens,
    setProduct,
    viewDecorations
  }
})
